import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeftRight } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';

export default function SupplierDuesWidget({ stats, pendingSalaries, branchDues, onViewDues, onRecordPayback }) {
  const s = stats || {};
  const ps = pendingSalaries || { totals: {}, employees: [], branch_summary: {}, period: '' };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <Card className="border-border border-warning/30 bg-warning/5">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Supplier Dues</CardTitle></CardHeader>
        <CardContent>
          <div className="text-2xl font-bold font-outfit text-warning inline-flex items-center gap-1" data-testid="supplier-dues"><RiyalIcon size="0.7em" /> {(s.supplier_dues || 0).toFixed(2)}</div>
          <p className="text-xs text-muted-foreground mt-1">Total owed to suppliers</p>
          {Array.isArray(s.supplier_dues_by_branch) && s.supplier_dues_by_branch.length > 0 && (
            <div className="mt-2 space-y-1" data-testid="supplier-dues-by-branch">
              {s.supplier_dues_by_branch.slice(0, 6).map((row) => (
                <div key={row.branch_id || 'unassigned'} className="flex justify-between items-center text-xs p-1.5 bg-background rounded" data-testid={`supplier-due-branch-${row.branch_id || 'unassigned'}`}>
                  <span className="truncate pr-2 text-foreground">
                    {row.branch_name}
                    <span className="text-[10px] text-muted-foreground ml-1">({row.supplier_count})</span>
                  </span>
                  <span className="font-bold text-warning inline-flex items-center gap-0.5"><RiyalIcon size="0.85em" /> {row.amount.toFixed(0)}</span>
                </div>
              ))}
              {s.supplier_dues_by_branch.length > 6 && (
                <p className="text-[10px] text-muted-foreground text-center pt-0.5">+{s.supplier_dues_by_branch.length - 6} more</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border border-orange-300 bg-orange-50">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Due Fines</CardTitle></CardHeader>
        <CardContent>
          <div className="text-2xl font-bold font-outfit text-orange-600 inline-flex items-center gap-1" data-testid="due-fines"><RiyalIcon size="0.7em" /> {(s.due_fines || 0).toFixed(2)}</div>
          {s.due_fines_list?.length > 0 && (
            <div className="mt-2 space-y-1">{s.due_fines_list.map((f, i) => (
              <div key={i} className="flex justify-between text-xs p-1.5 bg-background rounded"><span className="capitalize">{f.department}</span><span className="font-bold text-orange-600 inline-flex items-center gap-0.5"><RiyalIcon size="0.85em" /> {f.amount.toFixed(0)}</span></div>
            ))}</div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border border-error/30 bg-error/5">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Due Salaries</CardTitle></CardHeader>
        <CardContent>
          <div className="text-2xl font-bold font-outfit text-error inline-flex items-center gap-1" data-testid="due-salaries"><RiyalIcon size="0.7em" /> {(ps.totals?.total_pending || 0).toFixed(2)}</div>
          <p className="text-xs text-muted-foreground mt-1">{ps.period} | {(ps.employees || []).filter(e => e.pending_salary > 0).length} employee(s)</p>
          {Object.keys(ps.branch_summary || {}).length > 0 && (
            <div className="mt-2 space-y-1">
              {Object.entries(ps.branch_summary).map(([bName, bs]) => (
                <div key={bName} className="flex justify-between text-xs p-1.5 bg-background rounded">
                  <span>{bName}</span>
                  <span><span className="text-success inline-flex items-center gap-0.5">Paid: <RiyalIcon size="0.85em" /> {bs.total_paid.toFixed(0)}</span> <span className="text-error font-bold inline-flex items-center gap-0.5">Due: <RiyalIcon size="0.85em" /> {bs.total_pending.toFixed(0)}</span></span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {branchDues && (Object.keys(branchDues.dues || {}).length > 0 || Object.keys(branchDues.paybacks || {}).length > 0) && (
        <Card className="border-border md:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="font-outfit text-base">Branch-to-Branch Dues</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-xl" onClick={onViewDues} data-testid="view-dues-detail-btn">View Details</Button>
                <Button size="sm" variant="outline" className="rounded-xl" onClick={onRecordPayback}><ArrowLeftRight size={14} className="mr-1" />Record Payback</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(branchDues.dues || {}).map(([key, amt]) => (
                <div key={key} className="flex justify-between items-center p-3 bg-error/5 rounded-lg border border-error/20 cursor-pointer hover:bg-error/10 transition-colors" onClick={onViewDues} data-testid="branch-due-item">
                  <span className="text-sm font-medium">{key}</span>
                  <span className="font-bold text-error inline-flex items-center gap-0.5"><RiyalIcon size="0.85em" /> {amt.toFixed(2)}</span>
                </div>
              ))}
              {Object.entries(branchDues.paybacks || {}).map(([key, amt]) => (
                <div key={key} className="flex justify-between items-center p-3 bg-success/5 rounded-lg border border-success/20 cursor-pointer hover:bg-success/10 transition-colors" onClick={onViewDues}>
                  <span className="text-sm font-medium">{key}</span>
                  <span className="font-bold text-success inline-flex items-center gap-0.5"><RiyalIcon size="0.85em" /> {amt.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between items-center p-3 bg-primary/10 rounded-lg border border-primary/20 mt-2">
                <span className="text-sm font-bold">Net Due</span>
                <span className="font-bold text-primary inline-flex items-center gap-0.5"><RiyalIcon size="0.85em" /> {((branchDues.total_dues || 0) - (branchDues.total_paybacks || 0)).toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
